import type { KnowledgeParticle } from '../../types/domain';
import {
  getStreamFamilyDecorations,
  type LearningEpisodeStream,
  type LearningEpisodeStreamCardDecoration,
} from '../../utils/subjectConfig';
import { extractStructuredTextParts } from '../../utils/textHelper';

export type RevisionFamilyDeckCardKind = 'home-question' | 'sub-question' | 'full-answer' | 'family-summary';

export type RevisionFamilyDeckCard = {
  id: string;
  particleId: string;
  cardKind?: RevisionFamilyDeckCardKind;
  streamDecorationKind?: 'opening' | 'family-summary' | null;
  title: string;
  questionMain: string;
  questionItems: string[];
  answerMain: string;
  answerItems: string[];
  topicKey: string;
  topicLabel: string;
  subjectLabel: string;
  familyIndex?: number;
  familyCardIndex?: number;
  familyCardCount?: number;
  isFamilyFinalCard?: boolean;
  familyFeedbackProtocol?: 'logical_testing' | 'memory_per_card';
  familyQuestionItems?: string[];
  familyLabel?: string | null;
  familySummary?: string | null;
  sharedProblemTitle?: string | null;
  sharedProblemContent?: string | null;
  fullAnswerSections?: Array<{ id: string; label: string; content?: string | null; answerLabel?: string | null; answer?: string | null }>;
  factoryDrip?: boolean;
};

function readMetadataText(metadata: Record<string, unknown> | undefined, keys: string[]) {
  if (!metadata) return '';
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function buildParticleTitle(particle: KnowledgeParticle, index: number) {
  const questionParts = extractStructuredTextParts(particle.questions, 'question');
  return questionParts.main || questionParts.items[0] || particle.title || particle.content || `知识颗粒 ${index + 1}`;
}

function buildParticleQuestionLines(particle: KnowledgeParticle) {
  const parts = extractStructuredTextParts(particle.questions, 'question');
  return { main: parts.main, items: parts.items };
}

function buildParticleAnswerLines(particle: KnowledgeParticle) {
  const parts = extractStructuredTextParts(particle.answers, 'answer');
  return { main: parts.main, items: parts.items };
}

function joinTextParts(main: string, items: string[]) {
  return [main, ...items].map((part) => part.trim()).filter(Boolean).join('\n');
}

function resolveTopicIdentity(particle: KnowledgeParticle, index: number) {
  const subject = String(particle.subject || '').trim();
  const rawLabel = readMetadataText(particle.metadata, ['topicLabel', 'topic', 'familyLabel', 'family_label'])
    || buildParticleTitle(particle, index);
  const label = subject ? `${subject} / ${rawLabel}` : rawLabel;
  return { key: label.toLowerCase(), label };
}

function isFactoryDripParticle(metadata: Record<string, unknown> | null | undefined): boolean {
  if (!metadata) return false;
  if (metadata.factoryDrip && typeof metadata.factoryDrip === 'object') return true;
  const workbench = metadata.ttqWorkbench && typeof metadata.ttqWorkbench === 'object'
    ? metadata.ttqWorkbench as Record<string, unknown>
    : null;
  return String(workbench?.origin || '') === 'factory_ipttq_drip';
}

function applyRevisionFamilyCardIndices(cards: RevisionFamilyDeckCard[]): RevisionFamilyDeckCard[] {
  const familyCardCount = cards.length;
  return cards.map((card, familyCardIndex) => ({
    ...card,
    familyCardIndex,
    familyCardCount,
    isFamilyFinalCard: familyCardIndex === familyCardCount - 1,
  }));
}

function buildBaselineRevisionFamilyCards(particle: KnowledgeParticle, index: number): RevisionFamilyDeckCard[] {
  const question = buildParticleQuestionLines(particle);
  const answer = buildParticleAnswerLines(particle);
  const topic = resolveTopicIdentity(particle, index);
  const metadata = particle.metadata;
  const sharedProblemContent = readMetadataText(metadata, [
    'sharedProblem', 'shared_problem', 'motherProblem', 'mother_problem', 'sourceQuestion', 'source_question',
  ]) || question.main || question.items[0] || particle.content || buildParticleTitle(particle, index);
  const sharedProblemTitle = readMetadataText(metadata, ['sharedProblemTitle', 'shared_problem_title', 'familyTitle']) || topic.label;
  const familySummary = readMetadataText(metadata, ['summary', 'brief', 'aiBrief', 'description']);
  const subQuestions = question.items.filter(Boolean);
  const fullAnswerText = joinTextParts(answer.main, answer.items) || '这套主问题暂未写入完整解答。';

  const common = {
    particleId: particle.id,
    topicKey: topic.key,
    topicLabel: topic.label,
    subjectLabel: String(particle.subject || '').trim() || '未分学科',
    familyIndex: index,
    familyLabel: topic.label,
    familyQuestionItems: subQuestions,
    familySummary,
    sharedProblemTitle,
    sharedProblemContent,
    factoryDrip: isFactoryDripParticle(metadata),
  };

  const subCards = subQuestions.map((subQuestion, subIndex) => ({
    ...common,
    id: `${particle.id}:sub:${subIndex}`,
    cardKind: 'sub-question' as const,
    streamDecorationKind: null,
    title: `子问题 ${subIndex + 1}`,
    questionMain: subQuestion,
    questionItems: [] as string[],
    answerMain: answer.items[subIndex] || '这张子卡暂未写入单独解析。',
    answerItems: [] as string[],
  }));

  return [
    ...subCards,
    {
      ...common,
      id: `${particle.id}:full-answer`,
      cardKind: 'full-answer' as const,
      streamDecorationKind: null,
      title: '查看完整主问题和答案',
      questionMain: '查看完整主问题和答案',
      questionItems: [],
      answerMain: fullAnswerText,
      answerItems: [],
      fullAnswerSections: [
        { id: `${particle.id}:main-question`, label: '主问题', content: sharedProblemContent },
        ...subCards.map((card, subIndex) => ({
          id: `${particle.id}:sub-answer:${subIndex}`,
          label: `子问题 ${subIndex + 1}`,
          content: card.questionMain,
          answerLabel: `子答案 ${subIndex + 1}`,
          answer: card.answerMain,
        })),
        { id: `${particle.id}:main-answer`, label: '完整主问题答案', content: fullAnswerText },
      ],
    },
  ];
}

function buildFamilySummaryDecorationCard(particle: KnowledgeParticle, index: number, baselineCards: RevisionFamilyDeckCard[]): RevisionFamilyDeckCard {
  const anchorCard = baselineCards[0] || buildBaselineRevisionFamilyCards(particle, index)[0];
  return {
    ...anchorCard,
    id: `${particle.id}:family-summary`,
    cardKind: 'family-summary',
    streamDecorationKind: 'family-summary',
    title: '给这套卡做个总结打分',
    questionMain: '这套卡刷完后，给一个总体分数，再补一句哪里顺、哪里卡。',
    questionItems: [],
    answerMain: '总结保存后就进入下一套卡族。',
    answerItems: [],
    fullAnswerSections: undefined,
  };
}

function buildHomepageQuestionDecorationCard(particle: KnowledgeParticle, index: number, baselineCards: RevisionFamilyDeckCard[]): RevisionFamilyDeckCard {
  const anchorCard = baselineCards[0] || buildBaselineRevisionFamilyCards(particle, index)[0];
  const homepageQuestion = anchorCard.sharedProblemContent || anchorCard.sharedProblemTitle || anchorCard.questionMain || buildParticleTitle(particle, index);
  return {
    ...anchorCard,
    id: `${particle.id}:homepage-question`,
    cardKind: 'home-question',
    streamDecorationKind: null,
    title: anchorCard.sharedProblemTitle || anchorCard.familyLabel || '首页问题',
    questionMain: homepageQuestion,
    questionItems: [],
    answerMain: '',
    answerItems: [],
    fullAnswerSections: undefined,
  };
}

function reshapeMemoryPerCardFamily(
  cards: RevisionFamilyDeckCard[],
  particle: KnowledgeParticle,
  index: number,
): RevisionFamilyDeckCard[] {
  const fullAnswer = cards.find((card) => card.cardKind === 'full-answer') || cards[cards.length - 1];
  if (!fullAnswer) {
    return cards.map((card) => ({ ...card, familyFeedbackProtocol: 'memory_per_card' as const }));
  }

  const stem = String(
    fullAnswer.sharedProblemContent
      || fullAnswer.sharedProblemTitle
      || buildParticleTitle(particle, index),
  ).trim();

  // One Practice KP → one blank-flip overview (triad). Keep 语/数/英 deliberate practice aligned.
  const overview: RevisionFamilyDeckCard = {
    ...fullAnswer,
    id: `${particle.id}:full-answer`,
    cardKind: 'full-answer',
    streamDecorationKind: null,
    title: '',
    questionMain: '',
    questionItems: [],
    familyQuestionItems: [],
    fullAnswerSections: undefined,
    familyFeedbackProtocol: 'memory_per_card',
    sharedProblemContent: stem || fullAnswer.sharedProblemContent,
  };

  return [overview];
}

function applyRevisionFamilyDecorations(
  baselineCards: RevisionFamilyDeckCard[],
  particle: KnowledgeParticle,
  index: number,
  decorations: LearningEpisodeStreamCardDecoration[],
): RevisionFamilyDeckCard[] {
  // 严格按当前 Stream 的 decorationKeys 逐项应用；禁止用某一项推断另一项（例如 ignore_sub ≠ opening）。
  let nextCards: RevisionFamilyDeckCard[] = baselineCards.map((card) => ({ ...card, streamDecorationKind: null }));

  if (decorations.includes('homepage-question') && !nextCards.some((card) => card.cardKind === 'home-question')) {
    nextCards = [buildHomepageQuestionDecorationCard(particle, index, nextCards), ...nextCards];
  }

  if (decorations.includes('ignore-sub-questions')) {
    nextCards = nextCards.filter((card) => card.cardKind !== 'sub-question');
  }

  if (decorations.includes('opening')) {
    const openingCardIndex = nextCards.findIndex((card) => card.cardKind === 'home-question');
    const fallbackOpeningCardIndex = openingCardIndex >= 0
      ? openingCardIndex
      : nextCards.findIndex((card) => card.cardKind !== 'family-summary');
    if (fallbackOpeningCardIndex >= 0) {
      nextCards = nextCards.map((card, candidateIndex) => (
        candidateIndex === fallbackOpeningCardIndex
          ? { ...card, streamDecorationKind: 'opening' }
          : card
      ));
    }
  }

  if (decorations.includes('family-summary')) {
    nextCards = [...nextCards, buildFamilySummaryDecorationCard(particle, index, nextCards)];
  }

  if (decorations.includes('memory-per-card')) {
    nextCards = reshapeMemoryPerCardFamily(nextCards, particle, index);
  }

  return applyRevisionFamilyCardIndices(nextCards);
}

export function buildRevisionFamilyCards(
  particle: KnowledgeParticle,
  index: number,
  stream: LearningEpisodeStream,
): RevisionFamilyDeckCard[] {
  const baselineCards = buildBaselineRevisionFamilyCards(particle, index);
  const decorations = getStreamFamilyDecorations(stream);
  return applyRevisionFamilyDecorations(baselineCards, particle, index, decorations);
}

export function buildRevisionFamilyDeck(particles: KnowledgeParticle[], stream: LearningEpisodeStream) {
  // Keep each family's cards contiguous so next/prev walks 子问题1→2→总览, not another family.
  return particles.flatMap((particle, index) => buildRevisionFamilyCards(particle, index, stream));
}

/** Optional topic-round-robin of whole families (not individual cards). */
export function interleaveFamiliesByTopic(cards: RevisionFamilyDeckCard[]) {
  if (cards.length <= 1) return cards;

  const familyBlocks = new Map<string, RevisionFamilyDeckCard[]>();
  const familyOrder: string[] = [];
  cards.forEach((card) => {
    const familyKey = `${card.particleId}::${card.topicKey}`;
    if (!familyBlocks.has(familyKey)) {
      familyBlocks.set(familyKey, []);
      familyOrder.push(familyKey);
    }
    familyBlocks.get(familyKey)!.push(card);
  });

  const topicBuckets = new Map<string, RevisionFamilyDeckCard[][]>();
  familyOrder.forEach((familyKey) => {
    const block = familyBlocks.get(familyKey) || [];
    const topicKey = block[0]?.topicKey || familyKey;
    const bucket = topicBuckets.get(topicKey) || [];
    bucket.push(block);
    topicBuckets.set(topicKey, bucket);
  });

  if (topicBuckets.size <= 1) {
    return cards;
  }

  const orderedBuckets = Array.from(topicBuckets.values()).map((bucket) => bucket.slice());
  const interleaved: RevisionFamilyDeckCard[] = [];
  while (orderedBuckets.some((bucket) => bucket.length > 0)) {
    orderedBuckets.forEach((bucket) => {
      const nextFamily = bucket.shift();
      if (nextFamily?.length) {
        interleaved.push(...nextFamily);
      }
    });
  }
  return interleaved;
}

/** @deprecated Prefer contiguous families via buildRevisionFamilyDeck; kept for callers. */
export function interleaveCardsByTopic(cards: RevisionFamilyDeckCard[]) {
  return interleaveFamiliesByTopic(cards);
}

export const FAMILY_SUMMARY_SCORE_OPTIONS = [
  { score: 35, label: '卡住', description: '入口没立住，主链路基本没带起来。' },
  { score: 60, label: '吃力', description: '能跟上，但切换步骤时很容易掉线。' },
  { score: 78, label: '走通', description: '主链路基本跑通，只剩局部卡点。' },
  { score: 92, label: '稳了', description: '这套卡已经带起来了，可以直接迁移。' },
] as const;

export { buildParticleTitle };
